import { z } from 'zod';

/**
 * Product catalog wire contracts.
 *
 * `LicensedProductPlan` keeps its full name everywhere a reader might otherwise
 * assume "plan" means the Verydia platform subscription a vendor buys from
 * Parallax Motion. Those are unrelated concepts (ADR-011), and VC-6 introduces
 * the other one.
 */

export const ProductDistributionSchema = z
  .enum(['SAAS', 'DESKTOP', 'MOBILE', 'CLI', 'API', 'SERVER', 'EMBEDDED', 'HYBRID', 'CUSTOM'])
  .describe(
    'How the vendor delivers this software. Describes what the product IS; it does not ' +
      'determine licensing or runtime policy.',
  );

export const CatalogStatusSchema = z.enum(['ACTIVE', 'ARCHIVED']);

export const productSchema = z
  .object({
    id: z.string(),
    organization_id: z.string(),
    name: z.string(),
    slug: z.string(),
    description: z.string().nullable(),
    distribution: ProductDistributionSchema,
    status: CatalogStatusSchema,
    /**
     * Send this back on the next edit. A mutation that does not state the version
     * it was written against is refused with 428, so a stale tab cannot silently
     * overwrite somebody else's change.
     */
    version: z.int().min(1),
    environment_count: z.int().min(0),
    licensed_product_plan_count: z.int().min(0),
    created_at: z.iso.datetime(),
    updated_at: z.iso.datetime(),
  })
  .meta({
    id: 'Product',
    title: 'Product',
    description: 'A piece of software a vendor licenses through Verydia Control.',
  });

export type ProductResource = z.infer<typeof productSchema>;

export const createProductRequestSchema = z
  .object({
    name: z.string().min(1).max(120),
    /** Required, with no default: only the vendor knows how their software ships. */
    distribution: ProductDistributionSchema,
    slug: z
      .string()
      .min(1)
      .max(48)
      .optional()
      .describe('Derived from the name when omitted. Unique within the organization.'),
    description: z.string().max(2_000).nullable().optional(),
  })
  .meta({ id: 'CreateProductRequest' });

export const updateProductRequestSchema = z
  .object({
    /**
     * The version the edit was written against. REQUIRED: omitting it is refused
     * with 428 rather than treated as "apply anyway", because last-write-wins
     * loses an administrator's change without telling anyone.
     */
    expected_version: z.int().min(1),
    name: z.string().min(1).max(120).optional(),
    description: z.string().max(2_000).nullable().optional(),
    distribution: ProductDistributionSchema.optional(),
    /**
     * Accepted so it can be REFUSED, not so it can be applied.
     *
     * A slug is permanent once set (ADR-017). Leaving it out of the schema would
     * have Zod strip it silently, so a caller who sent a new slug would receive
     * 200 and believe it had changed — the console says "Permanent once set" and
     * the API would appear to disagree. Naming it here makes the refusal
     * explicit, documented in OpenAPI, and enforced in the domain rather than by
     * a form that happens not to render the field.
     */
    slug: z.string().optional(),
  })
  .meta({ id: 'UpdateProductRequest' });

export const archiveProductRequestSchema = z
  .object({ expected_version: z.int().min(1) })
  .meta({ id: 'ArchiveProductRequest' });

/* ----------------------------- environments -------------------------------- */

/**
 * What the vendor calls this stage of their process. Descriptive only —
 * nothing behavioural keys off it.
 */
export const EnvironmentKindSchema = z.enum([
  'DEVELOPMENT',
  'TEST',
  'STAGING',
  'PILOT',
  'PRODUCTION',
  'CUSTOM',
]);

/**
 * The load-bearing one, and deliberately separate from `kind`.
 *
 * Future licence accounting reads this and nothing else. It is never derived
 * from the kind, the name or the slug: a vendor who calls production "live"
 * would otherwise be counted as having none, and one who calls a rehearsal
 * "production-mirror" would be counted twice (ADR-018).
 */
export const EnvironmentClassificationSchema = z.enum(['NON_PRODUCTION', 'PRODUCTION']);

export const environmentSchema = z
  .object({
    id: z.string(),
    organization_id: z.string(),
    product_id: z.string(),
    name: z.string(),
    slug: z.string(),
    kind: EnvironmentKindSchema,
    classification: EnvironmentClassificationSchema,
    status: CatalogStatusSchema,
    version: z.int().min(1),
    created_at: z.iso.datetime(),
    updated_at: z.iso.datetime(),
  })
  .meta({
    id: 'ProductEnvironment',
    title: 'Product environment',
    description:
      'A place a product runs — development, staging, production. Belongs to exactly one ' +
      'product, and carries an explicit production classification.',
  });

export type EnvironmentResource = z.infer<typeof environmentSchema>;

export const createEnvironmentRequestSchema = z
  .object({
    name: z.string().min(1).max(120),
    /** Optional: derived from the name when omitted, and permanent once set. */
    slug: z.string().min(1).max(48).optional(),
    kind: EnvironmentKindSchema,
    /**
     * Required, with no default and no derivation from `kind`. A console may
     * suggest one; the suggestion is visible and editable before it is sent.
     */
    classification: EnvironmentClassificationSchema,
  })
  .meta({ id: 'CreateProductEnvironmentRequest' });

export const updateEnvironmentRequestSchema = z
  .object({
    expected_version: z.int().min(1),
    name: z.string().min(1).max(120).optional(),
    kind: EnvironmentKindSchema.optional(),
    classification: EnvironmentClassificationSchema.optional(),
    /**
     * Accepted so it can be REFUSED, not applied. Leaving it out of the schema
     * would have Zod strip it silently, and a caller who sent a new slug would
     * get 200 and believe it changed (ADR-018).
     */
    slug: z.string().optional(),
  })
  .meta({ id: 'UpdateProductEnvironmentRequest' });

export const archiveEnvironmentRequestSchema = z
  .object({ expected_version: z.int().min(1) })
  .meta({ id: 'ArchiveProductEnvironmentRequest' });

/* -------------------------- licensed product plans ------------------------- */

export const LicensedProductPlanStatusSchema = z.enum(['DRAFT', 'ACTIVE', 'ARCHIVED']);

export const BillingIntervalSchema = z.enum(['ONE_TIME', 'MONTHLY', 'ANNUAL', 'CUSTOM']);

/** Why a licence cannot yet be issued against a plan. Additive. */
export const IssuanceBlockerSchema = z.enum([
  'NOT_ACTIVE',
  'LICENSE_TYPE_NOT_SET',
  'LICENSE_SCOPE_NOT_SET',
  'TERM_NOT_SET',
]);
export type IssuanceBlocker = z.infer<typeof IssuanceBlockerSchema>;

/*
  Spelled out rather than imported from the domain, matching how every other
  enum in this file is declared: contracts is the wire format and must not depend
  on the domain package. A guard asserts these stay identical to the domain's
  own lists, so the duplication cannot drift.
*/
export const LicenseTypeSchema = z.enum([
  'SUBSCRIPTION',
  'PERPETUAL',
  'TRIAL',
  'TIME_LIMITED',
  'USAGE_BASED',
  'HYBRID',
]);
export type LicenseType = z.infer<typeof LicenseTypeSchema>;

export const LicenseScopeSchema = z.enum([
  'TENANT',
  'ORGANIZATION',
  'USER',
  'DEVICE',
  'INSTANCE',
  'FLOATING_POOL',
]);
export type LicenseScope = z.infer<typeof LicenseScopeSchema>;

/**
 * How long a client may keep working from a cached licence.
 *
 * Expressing a policy here does NOT mean offline enforcement exists — runtime
 * validation is a separate capability, and this deployment reports it as
 * unavailable. A template may carry terms that only take effect once something
 * is there to enforce them.
 */
export const offlinePolicySchema = z
  .object({
    allowed: z.boolean(),
    max_snapshot_age_hours: z.int().min(0).max(8_760),
    allow_air_gapped_certificate: z.boolean(),
  })
  .meta({ id: 'OfflinePolicy' });

export const licensedProductPlanSchema = z
  .object({
    id: z.string(),
    organization_id: z.string(),
    product_id: z.string(),
    /** Integration-facing identity, permanent once set. */
    code: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    status: LicensedProductPlanStatusSchema,
    /**
     * COMPUTED. Never sent by a client, never stored.
     *
     * `status: "ACTIVE"` means the vendor has published this tier in their own
     * catalog. It does NOT mean a licence can be issued against it — that needs
     * terms the plan may not carry yet. Reading `status` as permission to issue
     * is the mistake this field exists to prevent (ADR-019).
     */
    issuance_ready: z.boolean(),
    /** Empty exactly when `issuance_ready`. Ordered most-important first. */
    issuance_blockers: z.array(IssuanceBlockerSchema),
    /*
      The issuance template.

      These are the terms a licence issued under this tier would START with. They
      are defaults on a reusable template, not an agreement with anyone: no
      customer and no environment is named here, because those are chosen when a
      licence is issued.

      `null` throughout means "not configured", never a default. A plan may sit
      ACTIVE in the catalog with none of them set — that is a published tier whose
      licence terms have not been decided yet, and it is a legitimate state.
    */
    license_type: LicenseTypeSchema.nullable(),
    license_scope: LicenseScopeSchema.nullable(),
    /** Days of validity. Required by types that end with time, forbidden on those that do not. */
    term_days: z.int().min(1).nullable(),
    seat_limit: z.int().min(1).nullable(),
    activation_limit: z.int().min(1).nullable(),
    offline_policy: offlinePolicySchema,
    /**
     * Minor units — 14900 is $149.00. `null` means no price has been recorded,
     * which is NOT the same as free; zero would be free.
     */
    list_price_minor: z.int().nullable(),
    /** ISO 4217. Null exactly when `list_price_minor` is null. */
    currency: z.string().nullable(),
    billing_interval: BillingIntervalSchema.nullable(),
    version: z.int().min(1),
    created_at: z.iso.datetime(),
    updated_at: z.iso.datetime(),
  })
  .meta({
    id: 'LicensedProductPlan',
    title: 'Licensed product plan',
    description:
      'A commercial tier a VENDOR defines for their own product — N-Expert.ai’s ' +
      '"Enterprise" for JurisMind. Not a Verydia platform subscription, and not an ' +
      'application entitlement set.',
  });

export type LicensedProductPlanResource = z.infer<typeof licensedProductPlanSchema>;

/**
 * Terms a licence issued under this tier would start with.
 *
 * Every one is nullable and optional: omitted leaves the current value alone,
 * explicit null clears it. Nothing here has a default — a licence type nobody
 * chose is not a licence type, and guessing one from a plan's name or price
 * would put a commercial decision into a vendor's catalog that they never made.
 */
const issuanceTemplateFields = {
  license_type: LicenseTypeSchema.nullable().optional(),
  license_scope: LicenseScopeSchema.nullable().optional(),
  term_days: z.int().min(1).max(36_500).nullable().optional(),
  // Positive integers only. Zero is not shorthand for unlimited — if unlimited
  // ever becomes a real semantic it gets modelled, not encoded as a magic value.
  seat_limit: z.int().min(1).max(10_000_000).nullable().optional(),
  activation_limit: z.int().min(1).max(10_000_000).nullable().optional(),
  offline_policy: offlinePolicySchema.optional(),
};

/** Optional pricing a vendor records about their own tier. Recorded, never charged. */
const commercialFields = {
  list_price_minor: z.int().min(0).nullable().optional(),
  currency: z
    .string()
    .regex(/^[A-Z]{3}$/, 'A currency must be a three-letter ISO 4217 code, uppercase.')
    .nullable()
    .optional(),
  billing_interval: BillingIntervalSchema.nullable().optional(),
};

export const createLicensedProductPlanRequestSchema = z
  .object({
    name: z.string().min(1).max(120),
    /** Optional: derived from the name when omitted, and permanent once set. */
    code: z.string().min(1).max(48).optional(),
    description: z.string().max(2_000).nullable().optional(),
    ...commercialFields,
  })
  .meta({ id: 'CreateLicensedProductPlanRequest' });

export const updateLicensedProductPlanRequestSchema = z
  .object({
    expected_version: z.int().min(1),
    name: z.string().min(1).max(120).optional(),
    description: z.string().max(2_000).nullable().optional(),
    ...commercialFields,
    ...issuanceTemplateFields,
    /**
     * Accepted so it can be REFUSED, not applied. Omitting it would have Zod
     * strip it silently, and a caller who sent a new code would get 200 and
     * believe it changed (ADR-019).
     */
    code: z.string().optional(),
  })
  .meta({ id: 'UpdateLicensedProductPlanRequest' });

export const planLifecycleRequestSchema = z
  .object({ expected_version: z.int().min(1) })
  .meta({ id: 'LicensedProductPlanLifecycleRequest' });

/* ----------------------------- catalog summary ------------------------------ */

/**
 * Organization-wide catalog totals.
 *
 * Deliberately organization-scoped and deliberately incomplete: it reports the
 * resource types that exist. Customers and licences are absent rather than zero,
 * because a zero says "the feature is here and nothing has been created", and
 * that is a different — false — statement.
 */
export const catalogSummaryResponseSchema = z
  .object({
    object: z.literal('catalog_summary'),
    organization_id: z.string(),
    products: z.object({
      /** Archived products are excluded. The console labels these counts "Active". */
      active: z.int().min(0),
    }),
    environments: z.object({
      active: z.int().min(0),
      /**
       * From `classification`, never from `kind`, name or slug. An environment
       * called "production" that is classified NON_PRODUCTION is not production,
       * and ADR-018 exists because the difference is load-bearing for accounting.
       */
      production: z.int().min(0),
      non_production: z.int().min(0),
    }),
    /**
     * Present because customers now exist as a resource type.
     *
     * Absent from this response until VC-3 shipped them — a zero would have said
     * "the feature is here and none have been created", which was false.
     */
    customers: z.object({ active: z.int().min(0) }),
    licensed_product_plans: z.object({
      active: z.int().min(0),
      /**
       * Computed with the same domain rule the plan pages use, never from a
       * stored column. ACTIVE is a lifecycle state; issuable is a question about
       * licence terms, and Checkpoint D exists to keep them apart.
       */
      issuance_ready: z.int().min(0),
    }),
  })
  .meta({ id: 'CatalogSummaryResponse' });

export type CatalogSummaryResponse = z.infer<typeof catalogSummaryResponseSchema>;
export type CatalogSummaryResource = CatalogSummaryResponse;
